import apiService from '@/api'
import type { Conversation, Message } from '@/types/domain'
import type { MessageSlot, Variant, StoreMessage } from '@/lib/messageSlots'
import { getActiveVariant, variantFromAlternative } from '@/lib/messageSlots'
import { reconcileResponseVariant } from '@/features/story-conversation/responseState'
import {
  pendingRoleplaySaves, retainRoleplaySave, forgetRoleplaySaves,
  isConfirmedSaveRejection, UserMessageSaveRejected, UserMessageSaveUncertain,
  type PendingRoleplaySave, type RoleplaySaveScope,
} from '@/features/roleplay-conversation/session/saveRecovery'

interface RecoveryHost {
  readonly epoch: number
  conversationId: number | null
  resources: { conversation: Conversation | null }
  slots: MessageSlot[]
  pendingSaves: PendingRoleplaySave[]
  recoveryStorageUnavailable: boolean
  slotById: (id: number | string) => MessageSlot | null
  addMessage: (message: StoreMessage) => void
  removeMessageLocal: (id: number | string) => void
  removeVariant: (id: number | string, variantId: number | string) => void
  reconcileMessageId: (tempId: number | string, realId: number | string, fields?: Partial<Pick<StoreMessage, 't' | 'created_at' | 'updated_at'>> | null) => void
  updateMessageLocal: (id: number | string, patch: Partial<Variant> & { director_note?: StoreMessage['director_note'] }) => void
}

/** Roleplay's durable-save/reconciliation policy around a retained browser copy.
 * Generation never enters here. Epoch checks affect projection, not original saves.
 */
export class RoleplaySaveRecovery {
  constructor(private readonly host: RecoveryHost) {}
  saveScope(): RoleplaySaveScope {
    const conversation = this.host.resources.conversation
    if (!conversation?.user_id) throw new Error('Conversation ownership is unavailable.')
    return {
      ownerId: conversation.user_id, conversationId: conversation.id,
      expectedTailId: this.host.slots.reduce<number | null>((tail, slot) => {
        const id = Number(slot.id)
        return Number.isFinite(id) ? Math.max(tail ?? 0, id) : tail
      }, null),
    }
  }

  syncPendingSaves(scope: RoleplaySaveScope) {
    if (scope.conversationId === this.host.conversationId && scope.ownerId === this.host.resources.conversation?.user_id) {
      this.host.pendingSaves = pendingRoleplaySaves(scope)
    }
  }

  retainSave(record: PendingRoleplaySave, epoch: number) {
    const durable = retainRoleplaySave(record)
    if (epoch !== this.host.epoch) return
    if (!durable) this.host.recoveryStorageUnavailable = true
    this.syncPendingSaves({ ownerId: record.ownerId, conversationId: record.request.conversationId, expectedTailId: null })
    const slot = this.host.slots.find(item => String(item.id) === String(record.slotId))
    const variant = slot?.variants.find(item => String(item.id) === String(record.variantId) || item.saveRequestId === record.request.requestId)
    if (variant) { variant.saveRequestId = record.request.requestId; variant.saveState = record.state }
  }

  applySavedMessage(record: PendingRoleplaySave, message: Message) {
    const realId = message.id
    if (record.request.parentMessageId == null) {
      const existing = this.host.slots.find(slot => String(slot.id) === String(realId))
      if (existing) {
        this.host.slots = this.host.slots.filter(slot => String(slot.id) !== String(record.slotId) || slot === existing)
      } else if (this.host.slotById(record.slotId)) {
        this.host.reconcileMessageId(record.slotId, realId, message)
      } else this.host.addMessage(message as unknown as StoreMessage)
      this.host.updateMessageLocal(realId, { content: message.content, phase: 'complete', director_note: message.director_note })
    } else {
      const slot = this.host.slotById(record.slotId)
      if (!slot) return
      const matches = (v: Variant) => String(v.id) === String(record.variantId) ||
        String(v.id) === String(realId) || v.saveRequestId === record.request.requestId
      const index = slot.variants.findIndex(matches)
      const local = slot.variants.find(v => v.saveRequestId === record.request.requestId) ?? slot.variants[index]
      const saved = { ...variantFromAlternative(message), saveRequestId: record.request.requestId, saveState: record.state }
      if (index < 0) slot.variants.push(saved)
      else {
        slot.variants = slot.variants.filter(v => !matches(v))
        slot.variants.splice(index, 0, reconcileResponseVariant(saved, local))
      }
      if (String(slot.activeVariantId) === String(record.variantId)) slot.activeVariantId = realId
    }
  }

  async saveCompletedMessage(record: PendingRoleplaySave, epoch: number): Promise<number | null> {
    const scope = { ownerId: record.ownerId, conversationId: record.request.conversationId, expectedTailId: record.request.expectedTailId }
    const retry = pendingRoleplaySaves(scope).some(item => item.request.requestId === record.request.requestId)
    let rejectionConfirmed = !retry
    record = { ...record, state: 'saving' }
    this.retainSave(record, epoch)
    try {
      const result = await apiService.saveRoleplayMessage(record.request).catch(async error => {
        if (record.request.isUserAuthor && retry && isConfirmedSaveRejection(error)) {
          return apiService.resolveRoleplayUserSave(record.request.conversationId, record.request.requestId)
        }
        throw error
      })
      if (result.status === 'rejected') {
        rejectionConfirmed = true
        throw new UserMessageSaveRejected()
      }
      if (result.status === 'deleted') {
        forgetRoleplaySaves(scope, [record.request.requestId])
        if (epoch === this.host.epoch) {
          if (record.request.parentMessageId == null) this.host.removeMessageLocal(record.slotId)
          else this.host.removeVariant(record.slotId, record.variantId)
          this.syncPendingSaves(scope)
        }
        return null
      }
      // Another session may have committed a different choice or deleted this
      // history while the request was in flight. Never recreate its recovery item.
      if (!pendingRoleplaySaves(scope).some(item => item.request.requestId === record.request.requestId)) {
        return Number(result.message.id)
      }
      record.savedMessageId = Number(result.message.id)
      this.retainSave(record, epoch)
      if (epoch !== this.host.epoch) {
        // Originals need no selection. Rerolls retain their choice for recovery
        // when this conversation is reopened, without touching another view.
        if (record.request.parentMessageId == null) forgetRoleplaySaves(scope, [record.request.requestId])
        else retainRoleplaySave({ ...record, state: 'pending' })
        return Number(result.message.id)
      }
      this.applySavedMessage(record, result.message)
      const slot = this.host.slotById(record.request.parentMessageId ?? result.message.id)
      if (record.request.parentMessageId != null && slot && String(slot.activeVariantId) === String(result.message.id)) {
        await apiService.selectMessageAlternative(record.request.parentMessageId, result.message.id)
      }
      forgetRoleplaySaves(scope, [record.request.requestId])
      if (epoch === this.host.epoch) {
        this.syncPendingSaves(scope)
        for (const item of this.host.slots) for (const variant of item.variants) {
          if (variant.saveRequestId === record.request.requestId) { delete variant.saveState; delete variant.saveRequestId }
        }
      }
      return Number(result.message.id)
    } catch (error) {
      if (!pendingRoleplaySaves(scope).some(item => item.request.requestId === record.request.requestId)) throw error
      if (record.request.isUserAuthor && rejectionConfirmed && isConfirmedSaveRejection(error)) {
        forgetRoleplaySaves(scope, [record.request.requestId])
        if (epoch === this.host.epoch) this.syncPendingSaves(scope)
        throw error
      }
      this.retainSave({ ...record, state: 'pending' }, epoch)
      if (record.request.isUserAuthor) throw new UserMessageSaveUncertain()
      throw error
    }
  }

  async restorePendingSaves(): Promise<void> {
    if (!this.host.resources.conversation?.user_id) return
    const epoch = this.host.epoch
    const scope = this.saveScope()
    for (let record of pendingRoleplaySaves(scope)) {
      record = { ...record, state: 'pending' }
      try {
        const result = await apiService.getRoleplayMessageSave(scope.conversationId, record.request.requestId)
        if (epoch !== this.host.epoch) return
        if (!pendingRoleplaySaves(scope).some(item => item.request.requestId === record.request.requestId)) continue
        if (result.status === 'deleted') { forgetRoleplaySaves(scope, [record.request.requestId]); continue }
        if (result.status === 'saved') {
          record.savedMessageId = Number(result.message.id)
          const slot = record.request.parentMessageId == null ? null : this.host.slotById(record.slotId)
          const selectionConfirmed = slot && String(slot.activeVariantId) === String(result.message.id) && !getActiveVariant(slot).saveState
          this.applySavedMessage(record, result.message)
          if (record.request.parentMessageId == null || !record.active || selectionConfirmed) {
            forgetRoleplaySaves(scope, [record.request.requestId])
            for (const slot of this.host.slots) for (const variant of slot.variants) {
              if (variant.saveRequestId === record.request.requestId) { delete variant.saveState; delete variant.saveRequestId }
            }
            continue
          }
        }
      } catch { if (epoch !== this.host.epoch) return }
      if (!pendingRoleplaySaves(scope).some(item => item.request.requestId === record.request.requestId)) continue
      const parentId = record.request.parentMessageId
      if (parentId == null) {
        const id = record.savedMessageId ?? record.slotId
        if (!this.host.slotById(id)) this.host.addMessage({ id, conversation_id: scope.conversationId,
          content: record.request.content, is_user_author: record.request.isUserAuthor, phase: 'complete',
          t: record.createdAt, director_note: record.request.directorNote } as unknown as StoreMessage)
      } else {
        const slot = this.host.slotById(parentId)
        if (!slot) continue
        const id = record.savedMessageId ?? record.variantId
        if (!slot.variants.some(v => String(v.id) === String(id))) slot.variants.push({
          id, origin: 'alternative', content: record.request.content, phase: 'complete', stream_id: record.request.streamId,
        })
        if (record.active) slot.activeVariantId = id
      }
      this.retainSave(record, epoch)
    }
    this.syncPendingSaves(scope)
  }

}
